/*#
# Sign-in: Firebase Auth for the board

A person at a browser signs in; an agent carries a token. The platform
accepts either as the bearer — a Firebase ID token resolves to the person's
roles by uid or email — so the board can use plain Firebase Auth (Google,
or email and password) and never handle a `tsp_…` token at all.

This module is the browser-only glue, and it has no build-time dependency:
the Firebase SDK is loaded on demand from Google's CDN, and the project's
public web config comes from Firebase Hosting's well-known
`/__/firebase/init.json`, with the project id read off the functions host
(`us-central1-<project>.cloudfunctions.net`). The API key in that config is
a public identifier of the project, not a secret; what protects the data is
the platform's roles, resolved per request from the ID token.

**If your page has a CSP:** the SDK loads from `https://www.gstatic.com`
(`firebasejs/<FIREBASE_SDK_VERSION>`) and the config from
`https://<project>.web.app`; allow both as script and connect sources.
No `unsafe-eval` is needed. SDK supply chain (a compromised CDN) is out
of scope here, as it is for any page that loads Firebase this way.

Sessions persist the way Firebase Auth persists them (this browser, until
sign-out), and ID tokens refresh themselves — the client asks for a fresh
one on every request. Sign-in domains other than `localhost` and the
project's own hosting must be authorized in the Firebase console.
*/

/*{ "parent": "Reference", "order": 96.5 }*/

export interface WebConfig {
  apiKey: string
  authDomain: string
  projectId: string
  appId: string
  [key: string]: unknown
}

/** `us-central1-virta-7e070.cloudfunctions.net` → `virta-7e070`; anything else → null. */
export function projectIdFromHost(host: string): string | null {
  const m =
    /^https?:\/\/[a-z]+-[a-z]+\d*-([a-z0-9-]+)\.cloudfunctions\.net(?:[/:]|$)/i.exec(
      host
    )
  return m ? m[1]! : null
}

/** The public web config Firebase Hosting serves for a project. */
export async function fetchWebConfig(
  projectId: string,
  fetchImpl: typeof fetch = fetch
): Promise<WebConfig> {
  const res = await fetchImpl(
    `https://${projectId}.web.app/__/firebase/init.json`
  )
  if (!res.ok)
    throw new Error(
      `virta: no web config for ${projectId} (${res.status}); is Firebase Hosting deployed?`
    )
  return (await res.json()) as WebConfig
}

export interface AuthUser {
  uid: string
  email: string
  name: string
}

export interface AuthSession {
  user: AuthUser
  /** a current ID token — refreshed by the SDK, so ask every time */
  idToken(): Promise<string>
  signOut(): Promise<void>
}

/** What the board needs from an auth backend; swapped for a fake in tests. */
export interface AuthProvider {
  /** the session this browser already has, if any */
  current(): Promise<AuthSession | null>
  google(): Promise<AuthSession>
  password(email: string, password: string): Promise<AuthSession>
}

export const FIREBASE_SDK_VERSION = '11.10.0'

/**
 * A runtime `import()` of a URL. A non-literal specifier is left to the
 * runtime by the bundler; no `Function` constructor, so a page with a CSP
 * that forbids `unsafe-eval` still works once `www.gstatic.com` is allowed.
 */
const importUrl = (url: string): Promise<Record<string, unknown>> =>
  import(/* @vite-ignore */ url) as Promise<Record<string, unknown>>

/**
 * Firebase Auth over the CDN SDK. Loads `firebase-app` and `firebase-auth`
 * once, initialises the app for the config, and wraps the three things the
 * board does with it.
 */
export async function firebaseAuth(
  config: WebConfig,
  loader: (url: string) => Promise<Record<string, unknown>> = importUrl
): Promise<AuthProvider> {
  const base = `https://www.gstatic.com/firebasejs/${FIREBASE_SDK_VERSION}`
  const [app, auth] = await Promise.all([
    loader(`${base}/firebase-app.js`),
    loader(`${base}/firebase-auth.js`),
  ])
  type App = Record<string, unknown>
  type User = {
    uid: string
    email: string | null
    displayName: string | null
    getIdToken(force?: boolean): Promise<string>
  }
  const initializeApp = app.initializeApp as (
    c: WebConfig,
    name?: string
  ) => App
  const getApps = app.getApps as () => App[]
  const getAuth = auth.getAuth as (a: App) => Record<string, unknown>
  const GoogleAuthProvider = auth.GoogleAuthProvider as new () => unknown
  const signInWithPopup = auth.signInWithPopup as (
    a: unknown,
    p: unknown
  ) => Promise<{ user: User }>
  const signInWithEmailAndPassword = auth.signInWithEmailAndPassword as (
    a: unknown,
    e: string,
    p: string
  ) => Promise<{ user: User }>
  const onAuthStateChanged = auth.onAuthStateChanged as (
    a: unknown,
    cb: (u: User | null) => void
  ) => () => void
  const signOut = auth.signOut as (a: unknown) => Promise<void>

  const existing = getApps().find(
    (a) => (a as { name?: string }).name === 'virta'
  )
  const instance = existing ?? initializeApp(config, 'virta')
  const authInstance = getAuth(instance)

  const session = (user: User): AuthSession => ({
    user: {
      uid: user.uid,
      email: user.email ?? '',
      name: user.displayName ?? user.email ?? user.uid,
    },
    idToken: () => user.getIdToken(),
    signOut: () => signOut(authInstance),
  })

  return {
    current: () =>
      new Promise<AuthSession | null>((resolve) => {
        const stop = onAuthStateChanged(authInstance, (user) => {
          stop()
          resolve(user ? session(user) : null)
        })
      }),
    google: async () =>
      session(
        (await signInWithPopup(authInstance, new GoogleAuthProvider())).user
      ),
    password: async (email, password) =>
      session(
        (await signInWithEmailAndPassword(authInstance, email, password)).user
      ),
  }
}
